import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useAnyHoverMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Icon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
import { type ReactNode, type Ref } from "react";

import { COMPASS_DEFAULT_INTERACTIVE_HOVER_CLASSES } from "../constants";

export interface HeaderProps {
  actions?: ReactNode;
  collapseButtonRef?: Ref<HTMLButtonElement>;
  isActionsOpen?: boolean;
  isCollapsed?: boolean;
  isDragEnabled?: boolean;
  isDragging?: boolean;
  onCollapseToggle?: () => void;
  title: string;
}

export const Header = ({
  actions,
  collapseButtonRef,
  isActionsOpen = false,
  isCollapsed = false,
  isDragEnabled = false,
  isDragging = false,
  onCollapseToggle,
  title,
}: HeaderProps) => {
  const canHover = useAnyHoverMediaQuery();

  return (
    <div
      className={classNames(
        "flex h-9 w-full items-center gap-1 rounded-md px-3 transition-colors duration-200",
        // Scope row hover to the collapse trigger button so hovering actions does not
        // highlight the header; group/header still reveals chevron and actions on row hover.
        "group/header has-[[data-collapse-trigger]:focus-visible]:bg-hover has-[[data-collapse-trigger]:hover]:bg-hover has-[[data-collapse-trigger]:focus-visible]:outline has-[[data-collapse-trigger]:focus-visible]:outline-2 has-[[data-collapse-trigger]:focus-visible]:outline-offset-2 has-[[data-collapse-trigger]:focus-visible]:outline-primary",
      )}
    >
      <button
        ref={collapseButtonRef}
        type="button"
        data-collapse-trigger
        className={classNames(
          "flex min-w-0 flex-1 items-center gap-1 focus-visible:outline-none",
          {
            "cursor-grab": isDragEnabled && !isDragging,
            "cursor-grabbing": isDragEnabled && isDragging,
          },
        )}
        aria-expanded={!isCollapsed}
        onClick={onCollapseToggle}
      >
        <Typography
          className="truncate"
          color="primary"
          component="span"
          variant="heading-sm"
        >
          {title}
        </Typography>
        <span
          className={classNames(
            "inline-flex shrink-0 items-center justify-center transition-transform",
            isCollapsed ? "rotate-0" : "rotate-180",
            canHover &&
              "hidden group-focus-within/header:inline-flex group-hover/header:inline-flex",
          )}
        >
          <Icon name="chevron-triangle-down" color="primary" size="md" />
        </span>
      </button>
      {actions && (
        <div
          className={classNames(
            "inline-flex shrink-0 items-center gap-1",
            canHover &&
              !isActionsOpen &&
              "hidden group-focus-within/header:inline-flex group-hover/header:inline-flex",
            COMPASS_DEFAULT_INTERACTIVE_HOVER_CLASSES,
          )}
          data-testid="space-group-actions"
        >
          {actions}
        </div>
      )}
    </div>
  );
};
